import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { COMPANY_INFO, CORE_SERVICES, MAIN_AREAS } from '../data/siteData';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const servicesTimeoutRef = useRef(null);
  const areasTimeoutRef = useRef(null);
  const location = useLocation();

  // Handle scroll state with threshold hysteresis to avoid flickering/jumping
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          // Use hysteresis: activate at 60px, deactivate when scrolled back up above 20px
          setIsScrolled((prev) => {
            if (!prev && scrollY > 60) return true;
            if (prev && scrollY < 20) return false;
            return prev;
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handlers for desktop dropdowns with safe delay to bridge mouse movement gap
  const handleServicesEnter = () => {
    if (servicesTimeoutRef.current) clearTimeout(servicesTimeoutRef.current);
    setServicesOpen(true);
  };
  const handleServicesLeave = () => {
    servicesTimeoutRef.current = setTimeout(() => {
      setServicesOpen(false);
    }, 180);
  };

  const handleAreasEnter = () => {
    if (areasTimeoutRef.current) clearTimeout(areasTimeoutRef.current);
    setAreasOpen(true);
  };
  const handleAreasLeave = () => {
    areasTimeoutRef.current = setTimeout(() => {
      setAreasOpen(false);
    }, 180);
  };

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
    setAreasOpen(false);
  }, [location.pathname]);

  // Prevent background body scroll when mobile menu is open, but keep drawer scrollable
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Determine active category states
  const pathname = location.pathname;

  const isServicesActive =
    pathname === '/services' ||
    CORE_SERVICES.some(
      (s) =>
        pathname === `/${s.slug}` ||
        pathname === `/ydelser/${s.slug}` ||
        (s.altSlug && pathname === `/${s.altSlug}`)
    );

  const isAreasActive =
    pathname === '/omraader' ||
    MAIN_AREAS.some(
      (a) =>
        pathname === `/${a.slug}` ||
        pathname === `/omraader/${a.slug}` ||
        a.localPages.some((lp) => pathname === `/${lp.slug}`)
    );

  return (
    <header className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur-md border-b border-[#e2ece6] shadow-xs transition-shadow duration-300">
      {/* Top Utility Bar - stays fixed height on desktop */}
      <div className="hidden min-[1200px]:block bg-[#163627] text-white text-xs py-2 px-4 sm:px-6 border-b border-[#0f241a]">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-3 sm:gap-6 flex-wrap">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[#d0e4d9]">
              <i className="fa-solid fa-award text-[#52b788]" aria-hidden="true" />
              <span>Certificerede arborister fra Skovskolen</span>
            </span>
            <span className="hidden md:inline-flex items-center gap-1.5 text-[#d0e4d9]">
              <i className="fa-solid fa-shield-check text-[#52b788]" aria-hidden="true" />
              <span>Forsikret for op til 10 mio. kr.</span>
            </span>
            <a
              href={COMPANY_INFO.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[#d0e4d9] hover:text-white transition-colors"
            >
              <i className="fa-solid fa-location-dot text-[#52b788]" aria-hidden="true" />
              <span>
                {COMPANY_INFO.address}, {COMPANY_INFO.postalCode} {COMPANY_INFO.city}
              </span>
            </a>
          </div>

          <div className="flex items-center gap-4 sm:gap-6 ml-auto">
            <a
              href={`mailto:${COMPANY_INFO.email}`}
              className="hidden lg:inline-flex items-center gap-1.5 text-[#d0e4d9] hover:text-white transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-[#52b788]" aria-hidden="true" />
              <span className="whitespace-nowrap">{COMPANY_INFO.email}</span>
            </a>
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="inline-flex items-center gap-1.5 font-semibold text-white hover:text-[#52b788] transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[#52b788]" aria-hidden="true" />
              <span>Ring alle dage: </span>
              <strong className="whitespace-nowrap underline decoration-[#52b788]/60 underline-offset-2">
                {COMPANY_INFO.phone}
              </strong>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className={`flex items-center justify-between gap-4 transition-[height] duration-300 ease-out ${isScrolled ? 'h-18 sm:h-20' : 'h-24 sm:h-26 lg:h-28'}`}>
          {/* Logo with smooth resizing and fixed wrapper height to prevent jumping/shaking */}
          <Link to="/" className="flex items-center gap-3 shrink-0 py-1" aria-label="Hærvejens Træpleje forside">
            <img
              src={COMPANY_INFO.logoUrl}
              alt="Hærvejens Træpleje Logo"
              className={`w-auto object-contain transition-all duration-300 ease-out max-h-[85px] ${
                isScrolled ? 'h-16 sm:h-18 max-h-[65px]' : 'h-26 sm:h-30 lg:h-34'
              }`}
            />
          </Link>

          {/* DESKTOP NAVIGATION: Visible ONLY from 1200px and up as explicitly requested! */}
          <nav
            aria-label="Hovedmenu"
            className="hidden min-[1200px]:flex items-center gap-1 lg:gap-2 text-[15px] font-medium"
          >
            {/* Forside */}
            <Link
              to="/"
              className={`px-3.5 py-2 rounded-lg transition-colors ${
                pathname === '/'
                  ? 'text-[#163627] font-semibold bg-[#e8f1ec]'
                  : 'text-[#28382e] hover:text-[#163627] hover:bg-[#f2f7f4]'
              }`}
            >
              Forside
            </Link>

            {/* Ydelser Dropdown */}
            <div
              className="relative py-2"
              onMouseEnter={handleServicesEnter}
              onMouseLeave={handleServicesLeave}
            >
              <button
                type="button"
                onClick={() => setServicesOpen(!servicesOpen)}
                aria-expanded={servicesOpen}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg transition-colors cursor-pointer ${
                  isServicesActive
                    ? 'text-[#163627] font-semibold bg-[#e8f1ec]'
                    : 'text-[#28382e] hover:text-[#163627] hover:bg-[#f2f7f4]'
                }`}
              >
                <span>Ydelser</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    servicesOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>

              {servicesOpen && (
                <div
                  className="absolute top-full left-0 pt-1.5 w-80 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                  onMouseEnter={handleServicesEnter}
                  onMouseLeave={handleServicesLeave}
                >
                  <div className="bg-white rounded-2xl shadow-xl border border-[#dce8e1] py-2 overflow-hidden">
                    <div className="px-4 py-2 border-b border-[#edf3ef] mb-1">
                      <p className="text-xs font-semibold text-[#52705e]">
                        Vores ydelser
                      </p>
                    </div>
                    {CORE_SERVICES.map((service) => {
                      const isThisActive = pathname === `/${service.slug}`;
                      return (
                        <Link
                          key={service.slug}
                          to={`/${service.slug}`}
                          onClick={() => setServicesOpen(false)}
                          className={`flex items-start gap-3 px-4 py-2.5 transition-colors ${
                            isThisActive
                              ? 'bg-[#e4eee8] font-bold text-[#163627] border-l-4 border-[#163627]'
                              : 'text-[#2b3a30] hover:bg-[#f2f7f4] hover:text-[#163627]'
                          }`}
                        >
                          <div
                            className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 text-xs ${
                              isThisActive
                                ? 'bg-[#163627] text-white shadow-xs'
                                : 'bg-[#e8f1ec] text-[#1f4a36]'
                            }`}
                          >
                            <i className={`fa-solid ${service.icon}`} aria-hidden="true" />
                          </div>
                          <div>
                            <div
                              className={`text-sm ${
                                isThisActive ? 'font-bold text-[#163627]' : 'font-medium text-[#14291d]'
                              }`}
                            >
                              {service.title}
                            </div>
                            <p className="text-xs text-[#5f7466] line-clamp-1">{service.intro}</p>
                          </div>
                        </Link>
                      );
                    })}
                    <div className="pt-1 mt-1 border-t border-[#edf3ef] px-3">
                      <Link
                        to="/services"
                        onClick={() => setServicesOpen(false)}
                        className={`block text-center py-2 text-xs font-semibold rounded-lg transition-colors ${
                          pathname === '/services'
                            ? 'bg-[#e4eee8] text-[#163627] font-bold'
                            : 'text-[#1f4a36] hover:bg-[#f2f7f4] hover:underline'
                        }`}
                      >
                        Se alle ydelser »
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Områder Dropdown */}
            <div
              className="relative py-2"
              onMouseEnter={handleAreasEnter}
              onMouseLeave={handleAreasLeave}
            >
              <button
                type="button"
                onClick={() => setAreasOpen(!areasOpen)}
                aria-expanded={areasOpen}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg transition-colors cursor-pointer ${
                  isAreasActive
                    ? 'text-[#163627] font-semibold bg-[#e8f1ec]'
                    : 'text-[#28382e] hover:text-[#163627] hover:bg-[#f2f7f4]'
                }`}
              >
                <span>Områder</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    areasOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>

              {areasOpen && (
                <div
                  className="absolute top-full left-0 pt-1.5 w-84 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                  onMouseEnter={handleAreasEnter}
                  onMouseLeave={handleAreasLeave}
                >
                  <div className="bg-white rounded-2xl shadow-xl border border-[#dce8e1] py-2 overflow-hidden">
                    <div className="px-4 py-2 border-b border-[#edf3ef] flex items-center justify-between">
                      <p className="text-xs font-semibold text-[#52705e]">
                        Geografiske områder
                      </p>
                      <Link
                        to="/omraader"
                        onClick={() => setAreasOpen(false)}
                        className={`text-xs font-semibold transition-colors ${
                          pathname === '/omraader' ? 'text-[#163627] font-bold underline' : 'text-[#1f4a36] hover:underline'
                        }`}
                      >
                        Områdeoversigt
                      </Link>
                    </div>
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {MAIN_AREAS.map((area) => {
                        const isThisActive = pathname === `/${area.slug}`;
                        return (
                          <Link
                            key={area.slug}
                            to={`/${area.slug}`}
                            onClick={() => setAreasOpen(false)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                              isThisActive
                                ? 'bg-[#e4eee8] font-bold text-[#163627] border-l-3 border-[#163627]'
                                : 'text-[#2b3a30] hover:bg-[#f2f7f4] hover:text-[#163627]'
                            }`}
                          >
                            <i className={`fa-solid fa-location-dot text-xs ${isThisActive ? 'text-[#163627]' : 'text-[#357959]'}`} aria-hidden="true" />
                            <span>{area.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                    <div className="border-t border-[#edf3ef] px-4 py-2.5 bg-[#fbfdfc] rounded-b-2xl">
                      <Link
                        to="/omraader"
                        onClick={() => setAreasOpen(false)}
                        className={`flex items-center justify-between text-xs font-semibold transition-colors ${
                          pathname === '/omraader'
                            ? 'text-[#163627] font-bold underline'
                            : 'text-[#1f4a36] hover:underline'
                        }`}
                      >
                        <span>Se alle byer & lokale landingssider</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Om os */}
            <Link
              to="/om-os"
              className={`px-3.5 py-2 rounded-lg transition-colors ${
                pathname === '/om-os'
                  ? 'text-[#163627] font-semibold bg-[#e8f1ec]'
                  : 'text-[#28382e] hover:text-[#163627] hover:bg-[#f2f7f4]'
              }`}
            >
              Om os
            </Link>

            {/* Referencer */}
            <Link
              to="/referencer"
              className={`px-3.5 py-2 rounded-lg transition-colors ${
                pathname === '/referencer'
                  ? 'text-[#163627] font-semibold bg-[#e8f1ec]'
                  : 'text-[#28382e] hover:text-[#163627] hover:bg-[#f2f7f4]'
              }`}
            >
              Referencer
            </Link>

            {/* Kontakt */}
            <Link
              to="/kontakt"
              className={`px-3.5 py-2 rounded-lg transition-colors ${
                pathname === '/kontakt'
                  ? 'text-[#163627] font-semibold bg-[#e8f1ec]'
                  : 'text-[#28382e] hover:text-[#163627] hover:bg-[#f2f7f4]'
              }`}
            >
              Kontakt
            </Link>
          </nav>

          {/* Right Header Actions */}
          <div className="hidden min-[1200px]:flex items-center gap-3">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1b4332] hover:bg-[#143627] text-white font-semibold text-sm shadow-sm transition-all duration-200 cursor-pointer"
            >
              <span>Få et tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* BURGER MENU BUTTON: Visible ONLY under 1200px as strictly required! */}
          <div className="flex min-[1200px]:hidden items-center gap-2">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              aria-label="Ring til Hærvejens Træpleje"
              className="w-10 h-10 rounded-xl bg-[#e8f1ec] text-[#1b4332] flex items-center justify-center text-sm font-semibold hover:bg-[#d5e7dd] transition-colors"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
              aria-expanded={mobileMenuOpen}
              className="w-11 h-11 rounded-xl border border-[#cfdcd4] bg-white text-[#163627] flex items-center justify-center text-xl hover:bg-[#f2f7f4] transition-colors cursor-pointer"
            >
              <i className={mobileMenuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE DRAWER: Visible when mobileMenuOpen is true (under 1200px) */}
      {mobileMenuOpen && (
        <div
          data-testid="mobile-menu-drawer"
          className="min-[1200px]:hidden fixed inset-0 z-[100] bg-white flex flex-col w-screen h-screen overflow-hidden"
          style={{ height: '100dvh' }}
        >
          {/* Drawer Header with Logo and Close button */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-[#e2ece6] bg-white shrink-0">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3"
              aria-label="Hærvejens Træpleje forside"
            >
              <img
                src={COMPANY_INFO.logoUrl}
                alt="Hærvejens Træpleje Logo"
                className="h-14 sm:h-16 w-auto object-contain"
              />
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Luk menu"
              className="w-11 h-11 rounded-xl border border-[#cfdcd4] bg-white text-[#163627] flex items-center justify-center text-xl hover:bg-[#f2f7f4] transition-colors cursor-pointer"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>

          {/* Scrollable menu body - takes remaining height, scrolls down when content overflows */}
          <div
            data-testid="mobile-menu-scroll"
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-6"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="max-w-lg mx-auto pb-24 space-y-4">
            {/* Quick action buttons - stacked vertically on mobile screens */}
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[#1b4332] text-white font-semibold text-sm shadow-xs whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring {COMPANY_INFO.phone}</span>
              </a>
              <Link
                to="/kontakt"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-[#1b4332] text-[#1b4332] font-semibold text-sm hover:bg-[#f2f7f4]"
              >
                <i className="fa-solid fa-file-pen" aria-hidden="true" />
                <span>Få et tilbud</span>
              </Link>
            </div>

            {/* Links list */}
            <div className="flex flex-col divide-y divide-[#edf3ef] text-base font-medium">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`py-3 px-2 flex items-center justify-between rounded-lg transition-colors ${
                  pathname === '/'
                    ? 'bg-[#e4eee8] text-[#163627] font-bold border-l-4 border-[#163627]'
                    : 'text-[#233127] hover:bg-[#f2f7f4]'
                }`}
              >
                <span>Forside</span>
                <i className="fa-solid fa-chevron-right text-xs text-[#8da395]" aria-hidden="true" />
              </Link>

              {/* Ydelser accordion section in mobile drawer */}
              <div className="py-3 px-2">
                <div className="flex items-center justify-between font-bold text-[#14291d] mb-2">
                  <span className="text-sm font-semibold text-[#52705e]">Ydelser</span>
                  <Link
                    to="/services"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-xs font-semibold px-2 py-1 rounded transition-colors ${
                      pathname === '/services'
                        ? 'bg-[#e4eee8] text-[#163627] font-bold underline'
                        : 'text-[#296046] hover:underline'
                    }`}
                  >
                    Se alle ydelser
                  </Link>
                </div>
                <div className="grid grid-cols-1 gap-1.5 pl-1 mt-2">
                  {CORE_SERVICES.map((s) => {
                    const isThisActive = pathname === `/${s.slug}`;
                    return (
                      <Link
                        key={s.slug}
                        to={`/${s.slug}`}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`py-2 px-3 rounded-lg flex items-center gap-2.5 text-sm transition-colors ${
                          isThisActive
                            ? 'bg-[#e4eee8] font-bold text-[#163627] border-l-4 border-[#163627]'
                            : 'text-[#38483e] hover:bg-[#f2f7f4] hover:text-[#163627]'
                        }`}
                      >
                        <i
                          className={`fa-solid ${s.icon} text-xs w-4 text-center ${
                            isThisActive ? 'text-[#163627]' : 'text-[#296046]'
                          }`}
                          aria-hidden="true"
                        />
                        <span>{s.title}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Områder section in mobile drawer */}
              <div className="py-3 px-2">
                <div className="flex items-center justify-between font-bold text-[#14291d] mb-2">
                  <span className="text-sm font-semibold text-[#52705e]">Områder vi dækker</span>
                  <Link
                    to="/omraader"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-xs font-semibold px-2 py-1 rounded transition-colors ${
                      pathname === '/omraader'
                        ? 'bg-[#e4eee8] text-[#163627] font-bold underline'
                        : 'text-[#296046] hover:underline'
                    }`}
                  >
                    Områdehub
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-1.5 pl-1 mt-2">
                  {MAIN_AREAS.map((a) => {
                    const isThisActive = pathname === `/${a.slug}`;
                    return (
                      <Link
                        key={a.slug}
                        to={`/${a.slug}`}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`py-2 px-2.5 rounded-lg flex items-center gap-2 text-sm transition-colors ${
                          isThisActive
                            ? 'bg-[#e4eee8] font-bold text-[#163627] border-l-3 border-[#163627]'
                            : 'text-[#38483e] hover:bg-[#f2f7f4] hover:text-[#163627]'
                        }`}
                      >
                        <i
                          className={`fa-solid fa-location-dot text-xs ${
                            isThisActive ? 'text-[#163627]' : 'text-[#296046]'
                          }`}
                          aria-hidden="true"
                        />
                        <span>{a.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              <Link
                to="/om-os"
                onClick={() => setMobileMenuOpen(false)}
                className={`py-3 px-2 flex items-center justify-between rounded-lg transition-colors ${
                  pathname === '/om-os'
                    ? 'bg-[#e4eee8] text-[#163627] font-bold border-l-4 border-[#163627]'
                    : 'text-[#233127] hover:bg-[#f2f7f4]'
                }`}
              >
                <span>Om Hærvejens Træpleje</span>
                <i className="fa-solid fa-chevron-right text-xs text-[#8da395]" aria-hidden="true" />
              </Link>

              <Link
                to="/referencer"
                onClick={() => setMobileMenuOpen(false)}
                className={`py-3 px-2 flex items-center justify-between rounded-lg transition-colors ${
                  pathname === '/referencer'
                    ? 'bg-[#e4eee8] text-[#163627] font-bold border-l-4 border-[#163627]'
                    : 'text-[#233127] hover:bg-[#f2f7f4]'
                }`}
              >
                <span>Referencer & Galleri</span>
                <i className="fa-solid fa-chevron-right text-xs text-[#8da395]" aria-hidden="true" />
              </Link>

              <Link
                to="/kontakt"
                onClick={() => setMobileMenuOpen(false)}
                className={`py-3 px-2 flex items-center justify-between rounded-lg transition-colors ${
                  pathname === '/kontakt'
                    ? 'bg-[#e4eee8] text-[#163627] font-bold border-l-4 border-[#163627]'
                    : 'text-[#233127] hover:bg-[#f2f7f4]'
                }`}
              >
                <span>Kontakt os</span>
                <i className="fa-solid fa-chevron-right text-xs text-[#8da395]" aria-hidden="true" />
              </Link>
            </div>

            {/* Bottom info */}
            <div className="pt-4 border-t border-[#edf3ef] text-xs text-[#526357] space-y-2">
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-location-dot text-[#296046]" aria-hidden="true" />
                <span>
                  {COMPANY_INFO.address}, {COMPANY_INFO.postalCode} {COMPANY_INFO.city}
                </span>
              </p>
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-envelope text-[#296046]" aria-hidden="true" />
                <a href={`mailto:${COMPANY_INFO.email}`} className="underline">
                  {COMPANY_INFO.email}
                </a>
              </p>
            </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
